import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {Trans} from 'react-i18next';
import {UNIT} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import ConsentBannerKr from '@di-shared/features/home/kr/ConsentBanner.kr';
import ConsentBannerLoadingKr from '@di-shared/features/home/kr/ConsentBannerLoading.kr';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import useUbi, {UbiCase} from '@di-shared/features/ubi/useUbi';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';

export interface UbiContentsProps {
  vin: string;
}

function UbiContents({vin}: UbiContentsProps) {
  const {t} = useInferableTranslation();
  const {ubiData, displayInsurancesData, getDiscountPredictionCase} = useUbi({vin});
  const {ubiCase, ...args} = getDiscountPredictionCase();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();

  async function openInsuranceInfoBottomSheet() {
    await ccWebviewUtils.openBottomSheetWebViewUrl<number>(ROUTE_PATHS.BOTTOM_SHEET_UBI_NO_INSURANCE.getFullPath());
  }

  function InsuranceCompanyLogo({serviceId}: {serviceId: string}) {
    switch (serviceId) {
      case import.meta.env.VITE_SERVICE_ID_AXA:
        return (
          <div className="insurance_logo">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_axa.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_axa.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_axa.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_axa.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_axa.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_axa.png')}
            />
          </div>
        );
      case import.meta.env.VITE_SERVICE_ID_HYUNDAI:
        return (
          <div className="insurance_logo">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_hyundai.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_hyundai.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_hyundai.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_hyundai.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_hyundai.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_hyundai.png')}
            />
          </div>
        );
      case import.meta.env.VITE_SERVICE_ID_DB:
        return (
          <div className="insurance_logo logo_border">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_db.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_db.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_db.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_db.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_db.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_db.png')}
            />
          </div>
        );
      case import.meta.env.VITE_SERVICE_ID_KB:
        return (
          <div className="insurance_logo">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_kb.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_kb.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_kb.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_kb.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_kb.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_kb.png')}
            />
          </div>
        );
      case import.meta.env.VITE_SERVICE_ID_HANA:
        return (
          <div className="insurance_logo logo_border">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_hana.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_hana.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_hana.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_hana.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_hana.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_hana.png')}
            />
          </div>
        );
      case import.meta.env.VITE_SERVICE_ID_CARROT:
        return (
          <div className="insurance_logo">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_carrot.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_carrot.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_carrot.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_carrot.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_carrot.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_carrot.png')}
            />
          </div>
        );
      case import.meta.env.VITE_SERVICE_ID_SAMSUNG:
        return (
          <div className="insurance_logo logo_border">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_samsung.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_samsung.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_samsung.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_samsung.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_samsung.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_samsung.png')}
            />
          </div>
        );
      case import.meta.env.VITE_SERVICE_ID_HANWHA:
        return (
          <div className="insurance_logo logo_border">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_hanwha.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_hanwha.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_hanwha.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_hanwha.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_hanwha.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_hanwha.png')}
            />
          </div>
        );
      case import.meta.env.VITE_SERVICE_ID_HEUNGKUK:
        return (
          <div className="insurance_logo logo_border">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_heungkuk.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_heungkuk.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_heungkuk.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_heungkuk.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_heungkuk.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_heungkuk.png')}
            />
          </div>
        );
      case import.meta.env.VITE_SERVICE_ID_LOTTE:
        return (
          <div className="insurance_logo logo_border">
            <ImageSelector
              alt={`${serviceId}-logo`}
              kiaLightSrc={() => import('@di-shared/assets/img/ubi/logo_lotteins.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/ubi/logo_lotteins.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/ubi/logo_lotteins.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/ubi/logo_lotteins.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/ubi/logo_lotteins.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/ubi/logo_lotteins.png')}
            />
          </div>
        );
      default:
        return <></>;
    }
  }

  function getDiscountPredictionMessage() {
    switch (ubiCase) {
      case 2:
        return {
          title: t('T20039_$n', {
            n: `${convertDistanceValue(args.N)}${convertDistanceSymbol(args.N)}`,
          }),
          description: '',
        };
      case 3:
        return {
          title: t('T20329_$n', {n: `${convertDistanceValue(args.N)}${convertDistanceSymbol(args.N)}`}),
          description: '',
        };
      case 4:
        return {
          title: t('T20188'),
          description: t('T20033_$n', {n: args.n}),
        };
      case 5:
        return {
          title: t('T20189_$a_$b', {a: args.a, b: args.b}),
          description: t('T20035_$c_$d', {
            c: `${convertDistanceValue(args.d)}${convertDistanceSymbol(args.d)}`,
            d: args.f,
          }),
        };
      case 6:
        return {
          title: t('T20036_$b', {b: args.c}),
          description: t('T20190_$c_$e', {
            c: `${convertDistanceValue(args.e)}${convertDistanceSymbol(args.e)}`,
            e: args.g,
          }),
        };
      case 7:
        return {
          title: t('T20090_$a_$b', {a: args.a, b: args.b}),
          description: t('T20192_$c', {c: args.c}),
        };
      case 8:
        return {
          title: t('T20090_$a_$b', {a: args.d, b: args.e}),
          description: t('T20193'),
        };
      case 9:
        return {
          title: t('T20191_$a_$b', {a: args.a, b: args.b}),
          description: t('T20037_$c_$d', {
            c: `${convertDistanceValue(args.c) + convertDistanceSymbol(args.c)}`,
            d: args.d,
          }),
        };
      case 10:
        return {
          title: t('T20038'),
          description: t('T20037_$c_$d', {
            c: `${convertDistanceValue(args.c) + convertDistanceSymbol(args.c)}`,
            d: args.d,
          }),
        };
      default:
        return null;
    }
  }

  return (
    <main className="content page_ubi">
      <ErrorBoundary fallback={<></>}>
        <Suspense fallback={<ConsentBannerLoadingKr />}>
          <ConsentBannerKr vin={vin} />
        </Suspense>
      </ErrorBoundary>
      <section className="section">
        <div className="section_heading type_main">
          <h2
            className="section_heading_title"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T20020')}
          </h2>
        </div>
        <div className="section_container">
          <div className="ubi_my">
            <ul className="ubi_my_list">
              <li className="ubi_my_item">
                <span className="ubi_my_key">{t('T20021')}</span>
                <span className="ubi_my_val">{t('T20006_$score', {score: ubiData.safetyDrvScore})}</span>
              </li>
              <li className="ubi_my_item">
                <span className="ubi_my_key">{t('T20022')}</span>
                <span className="ubi_my_val">
                  {formatNumberWithCommas(convertDistanceValue(ubiData.rangeDrvDist))}
                  {convertDistanceSymbol(ubiData.rangeDrvDist)}
                </span>
              </li>
            </ul>
            {ubiCase !== UbiCase.CASE_1 && ubiCase !== UbiCase.CASE_11 && getDiscountPredictionCase() && (
              <div className="section_container_box">
                <span
                  className="ubi_my_notice_title"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {getDiscountPredictionMessage()?.title}
                </span>
                <span
                  className={'ubi_my_notice_desc'}
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {getDiscountPredictionMessage()?.description}
                </span>
              </div>
            )}
          </div>
        </div>

        <ul className="insurance_list">
          {displayInsurancesData.map((insuranceItem, index: number) => (
            <React.Fragment key={insuranceItem.insuranceCompany}>
              <li className="insurance_item">
                <LinkTo
                  to={ROUTE_PATHS.UBI_DETAIL_$SERVICE_ID.getFullPath({
                    vin,
                    serviceId: insuranceItem.serviceId,
                  })}
                  className="insurance_link"
                  onClick={() =>
                    emitEventLog({
                      event: 'di_benefits',
                      data_action: 'view banner',
                      data_name: insuranceItem.insuranceCompany,
                    })
                  }
                >
                  <InsuranceCompanyLogo serviceId={insuranceItem.serviceId} />
                  <div className="insurance_info">
                    <p className="insurance_title">{insuranceItem.insuranceCompany}</p>
                    <div className="badge_box">
                      {insuranceItem.connectServiceDiscount > 0 && (
                        <span className="badge badge_outline">
                          <Trans
                            i18nKey={'T20024_$discount'}
                            values={{discount: `${insuranceItem.connectServiceDiscount}`}}
                            components={{
                              Highlighted: (
                                <span
                                  className="text_emphasis"
                                  style={{paddingLeft: '5px'}}
                                />
                              ),
                            }}
                          />
                        </span>
                      )}
                      {insuranceItem.safeDrivingScoreDiscount > 0 && (
                        <span className="badge badge_outline">
                          <Trans
                            i18nKey={'T20026_$discount'}
                            values={{discount: `${insuranceItem.safeDrivingScoreDiscount}`}}
                            components={{
                              Highlighted: (
                                <span
                                  className="text_emphasis"
                                  style={{paddingLeft: '5px'}}
                                />
                              ),
                            }}
                          />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="insurance_rate">
                    <span className="insurance_rate_percent">
                      {insuranceItem.totalDiscount}
                      {UNIT.PERCENT}
                    </span>
                    <span className="insurance_rate_txt">{t('T20027')}</span>
                  </div>
                </LinkTo>
              </li>
              {index === 4 && (
                <li
                  className="insurance_item type_banner"
                  onClick={() => {
                    emitEventLog({
                      event: 'di_vehicle_management',
                      data_action: 'view banner',
                      data_name: t('T20040'),
                    });
                    openInsuranceInfoBottomSheet();
                  }}
                >
                  <div className="driving_banner">
                    <button
                      type="button"
                      className="driving_banner_box"
                    >
                      <span className="driving_banner_txtbox type_reverse">
                        <strong className="driving_banner_title body_large_b">{t('T20040')}</strong>
                        <span className="driving_banner_desc body_small_r">{t('T20041')}</span>
                      </span>
                      <span className="driving_banner_imgbox">
                        <ImageSelector
                          className="driving_banner_img"
                          alt="driving_banner_img"
                          kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                          kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                          hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                          hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                          genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                          genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_insure_benefit.png')}
                        />
                      </span>
                    </button>
                  </div>
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </section>

      <div className="divider divider_thick"></div>
      <section className="section section_notice">
        <ul className="notice_list">
          <li className="notice_item">{t('T20028')}</li>
          <li className="notice_item">{t('T20029')}</li>
          <li className="notice_item">{t('T20030')}</li>
        </ul>
      </section>
    </main>
  );
}

export default UbiContents;
